/**
 * memberMessageTemplates.js  —  B2C: Academia → Aluno
 *
 * Templates WhatsApp para cada motivo de contato do kanban operacional.
 * Cada template:
 *   label          → nome exibido no seletor
 *   message        → texto pronto ({name}, {academyName}, {days}, {amount}, {date}, {referralName})
 *   consultantAction → instrução interna (NÃO é enviada ao aluno)
 *   expectedOutcomes → resultados a registrar após o contato
 *   nextStep        → próximo passo se não responder
 *   isBreakup       → true = última mensagem da sequência (não insistir mais)
 */

import { CONTACT_REASON } from "./tasks";

export const MEMBER_MESSAGE_TEMPLATES = {

    // ══════════════════════════════════════════════════════════════════════════
    // RENOVAÇÃO — matrícula vencendo ou vencida
    // ══════════════════════════════════════════════════════════════════════════
    [CONTACT_REASON.RENEWAL]: [
        {
            label: "Aviso Antecipado (D-7)",
            message: "Olá, *{name}*! Passando para avisar que sua matrícula na *{academyName}* vence em *{days} dias*. Para não perder seu horário, você pode renovar com antecedência. Quer que eu te ajude? 😊",
            consultantAction: "Contatar 7 dias antes. Se renovar → Feito. Se pedir prazo → agendar retorno. Tom leve, sem urgência ainda.",
            expectedOutcomes: ["renewed", "asked_deadline", "callback_later", "no_answer"],
            nextStep: "Se não responder em 2 dias, ligar.",
        },
        {
            label: "No Dia do Vencimento",
            message: "*{name}*, hoje é o último dia da sua matrícula na *{academyName}*! Para garantir seu horário, podemos renovar agora. Como prefere pagar? 💪",
            consultantAction: "Prioridade alta. Ter link de pagamento ou PIX em mãos para enviar junto. Ligar se não responder em 2h.",
            expectedOutcomes: ["renewed", "asked_deadline", "gave_up", "no_answer"],
            nextStep: "Ligar se não responder. Após 2 tentativas, usar template 'Após Vencimento'.",
        },
        {
            label: "Após Vencimento (até D+7)",
            message: "Oi, *{name}*! Tudo bem? 😊 Passando para te avisar que o plano de aulas na *{academyName}* venceu há alguns dias. Para evitar qualquer interrupção e garantir a continuidade das aulas, podemos seguir com a renovação? Posso te enviar as opções de plano para escolherem a melhor forma! 🏊‍♀️",
            consultantAction: "Tom empático. NÃO cobrar — entender o contexto. Se houver objeção de preço, apresentar desconto ou plano menor. Registrar o motivo claramente.",
            expectedOutcomes: ["renewed", "asked_deadline", "gave_up", "requested_discount", "no_answer"],
            nextStep: "Após 3 tentativas sem resposta → usar break-up.",
        },
        {
            label: "Encerramento de Contato (break-up)",
            message: "*{name}*, última tentativa por aqui! Boa sorte e se um dia quiser voltar à *{academyName}*, é só nos chamar — sem burocracia. 🙏",
            consultantAction: "ÚLTIMA MENSAGEM. Não contatar mais por iniciativa própria. Registrar 'Sem Resposta / Inatividade' (ou 'Desistiu' se houve recusa clara) e arquivar o card.",
            expectedOutcomes: ["renewed", "gave_up", "no_response_archived", "no_answer"],
            nextStep: "Arquivar. Reabrir somente se o aluno entrar em contato.",
            isBreakup: true,
        },
    ],

    // ══════════════════════════════════════════════════════════════════════════
    // REATIVAÇÃO — aluno que saiu e pode voltar
    // ══════════════════════════════════════════════════════════════════════════
    [CONTACT_REASON.REACTIVATION]: [
        {
            label: "Reativação Empática (1ª tentativa)",
            message: "Oi, *{name}*! Sentimos sua falta na *{academyName}*! Faz *{days} dias* que você não aparece. Está tudo bem? Se quiser retomar, estamos aqui para te ajudar a criar uma rotina que encaixe na sua vida agora. 🙌",
            consultantAction: "Tom leve, sem pressão. Objetivo: entender o motivo da ausência ANTES de oferecer qualquer plano. Escutar mais do que falar.",
            expectedOutcomes: ["enrolled", "callback_later", "asked_deadline", "gave_up", "no_answer"],
            nextStep: "Se responder que quer voltar mas não tem dinheiro → oferecer desconto de reativação.",
        },
        {
            label: "Oferta de Retorno (2ª tentativa)",
            message: "*{name}*, que tal dar uma nova chance à *{academyName}*? Temos uma *condição especial de reativação* para quem estava conosco antes. Posso te contar os detalhes? 🎁",
            consultantAction: "Usar somente se o aluno já demonstrou interesse ou não respondeu à 1ª mensagem. Ter desconto real para oferecer (ex: 1ª mensalidade com 20% ou isenção de taxa de matrícula).",
            expectedOutcomes: ["enrolled", "requested_discount", "callback_later", "no_answer"],
            nextStep: "Se não responder → break-up.",
        },
        {
            label: "Encerramento de Contato (break-up)",
            message: "*{name}*, prometo que é a última mensagem! 😊 Sentimos muito sua falta na *{academyName}*. Quando quiser voltar — hoje, mês que vem ou ano que vem — é só nos chamar. Estaremos aqui!",
            consultantAction: "ÚLTIMA MENSAGEM. Não contatar mais. Se responder positivamente após isso → reabrir com fluxo de matrícula.",
            expectedOutcomes: ["enrolled", "gave_up", "no_answer"],
            nextStep: "Arquivar. Reabrir somente se o aluno entrar em contato.",
            isBreakup: true,
        },
    ],

    // ══════════════════════════════════════════════════════════════════════════
    // RESGATE / RETENÇÃO — aluno quer cancelar
    // ══════════════════════════════════════════════════════════════════════════
    [CONTACT_REASON.RETENTION]: [
        {
            label: "Escuta Ativa (1ª abordagem)",
            message: "Olá, *{name}*! Soubemos que você está pensando em cancelar na *{academyName}*. Antes de qualquer decisão, posso entender o que aconteceu? Às vezes conseguimos resolver de um jeito que não imaginávamos. 💬",
            consultantAction: "NÃO oferecer desconto de imediato. Primeiro entender o motivo: preço, horário, professor, desmotivação, distância. Cada causa tem solução diferente.",
            expectedOutcomes: ["retained", "frozen", "gave_up", "callback_later", "no_answer"],
            nextStep: "Após entender o motivo, usar template específico para a causa.",
        },
        {
            label: "Contorno de Preço",
            message: "*{name}*, entendo que o valor pode pesar no orçamento. Verificamos aqui e temos *uma opção especial* para que você continue na *{academyName}* sem comprometer suas finanças. Posso te apresentar? 🤝",
            consultantAction: "Usar quando o motivo for preço. Ter autorização da gestão para oferecer: desconto temporário OU plano trimestral/semestral que dilui o custo mensal. Não prometer sem ter aprovação.",
            expectedOutcomes: ["retained", "requested_discount", "gave_up", "callback_later"],
            nextStep: "Se aceitar → criar nova matrícula e registrar 'Reteve'. Se recusar → break-up.",
        },
        {
            label: "Contorno de Horário / Professor",
            message: "*{name}*, entendi que o horário atual não está funcionando para você. Separei aqui algumas turmas que podem se encaixar melhor na sua rotina — posso te mandar? 📅",
            consultantAction: "Consultar grade disponível e enviar opções CONCRETAS junto com a mensagem. Não perguntar se quer mudar — já apresentar as opções prontas.",
            expectedOutcomes: ["retained", "gave_up", "scheduled_visit", "callback_later"],
            nextStep: "Agendar aula experimental na nova turma.",
        },
        {
            label: "Encerramento de Contato (break-up)",
            message: "Oi, *{name}*. Entendemos sua decisão e respeitamos muito. Se quiser voltar à *{academyName}* no futuro — quando o momento for melhor — a porta estará sempre aberta. Boa sorte! 🙏",
            consultantAction: "ÚLTIMA MENSAGEM. Tom de respeito total. Não tentar reverter novamente. Registrar motivo real da saída para relatório de retenção.",
            expectedOutcomes: ["retained", "gave_up"],
            nextStep: "Arquivar com tag do motivo de saída.",
            isBreakup: true,
        },
    ],

    // ══════════════════════════════════════════════════════════════════════════
    // COBRANÇA — débito em aberto
    // ══════════════════════════════════════════════════════════════════════════
    [CONTACT_REASON.BILLING]: [
        {
            label: "Lembrete Amigável (1ª tentativa)",
            message: "Olá, *{name}*! Identificamos um débito em aberto na *{academyName}* no valor de *R$ {amount}*. Você conseguiu verificar? Pode me avisar quando resolver! 😊",
            consultantAction: "Tom amigável — assumir que foi esquecimento. Enviar link de pagamento ou PIX junto. Registrar data e valor exatos no card.",
            expectedOutcomes: ["paid", "asked_deadline", "callback_later", "no_answer"],
            nextStep: "Aguardar 2 dias. Se não pagar → segunda tentativa.",
        },
        {
            label: "Segunda Tentativa",
            message: "*{name}*, retorno aqui sobre o débito de *R$ {amount}* na *{academyName}*. Quando consegue regularizar? Se precisar de um prazo diferente, é só me falar — quero encontrar uma forma de resolver. 🙏",
            consultantAction: "Se pediu prazo antes: confirmar a data combinada. Senão: dar opção concreta (PIX agora OU data até X dias). Não ameaçar suspensão ainda.",
            expectedOutcomes: ["paid", "asked_deadline", "gave_up", "no_answer"],
            nextStep: "Se não responder novamente → aviso de suspensão.",
        },
        {
            label: "Aviso de Suspensão (3ª tentativa)",
            message: "*{name}*, precisamos te avisar que o acesso à *{academyName}* será suspenso em *{days} dias* por débito de *R$ {amount}*. Para evitar a interrupção, regularize até *{date}*. Estou aqui para ajudar! 📋",
            consultantAction: "Usar após 2 tentativas sem sucesso. Informar data real de suspensão definida pela gestão. Tom claro mas respeitoso. Se pagar → reativar e registrar 'Pagou'.",
            expectedOutcomes: ["paid", "asked_deadline", "gave_up", "no_answer"],
            nextStep: "Após data limite sem pagamento → break-up.",
        },
        {
            label: "Encerramento de Contato (break-up)",
            message: "*{name}*, encerramos as tentativas de contato sobre o débito por aqui. Quando puder regularizar, é só nos chamar — sem julgamento, resolvemos em minutos. 😊",
            consultantAction: "ÚLTIMA MENSAGEM sobre cobrança. Não insistir mais. Encaminhar o caso para o processo padrão de inadimplência da academia (bloqueio de acesso etc.). Arquivar o card.",
            expectedOutcomes: ["paid", "gave_up"],
            nextStep: "Arquivar. Encaminhar para processo de inadimplência.",
            isBreakup: true,
        },
    ],

    // ══════════════════════════════════════════════════════════════════════════
    // EXPERIMENTAL — aluno agendou aula experimental e NÃO compareceu (no-show)
    // Fluxo: faltou → reagendar → última tentativa → break-up
    // ══════════════════════════════════════════════════════════════════════════
    [CONTACT_REASON.TRIAL_FOLLOWUP]: [
        {
            label: "Faltou à Aula Experimental (1ª tentativa)",
            message: "Oi, *{name}*! Vi que não conseguiu vir à aula experimental na *{academyName}* hoje 😊 Aconteceu algum imprevisto? Podemos remarcar sem problema — é só me avisar o melhor dia e horário!",
            consultantAction: "Tom leve, sem cobrar. Ter grade de horários disponíveis para enviar junto. Objetivo: remarcar, não vender ainda. Registrar 'Agendou Visita' se confirmar novo horário.",
            expectedOutcomes: ["scheduled_visit", "callback_later", "gave_up", "no_answer"],
            nextStep: "Se não responder em 2 dias → segunda tentativa.",
        },
        {
            label: "Segunda Tentativa de Reagendamento",
            message: "*{name}*, tentei falar com você sobre a aula experimental na *{academyName}*! A agenda ainda está aberta — qualquer horário que funcionar para você, a gente encaixa. 😊",
            consultantAction: "Propor 2-3 opções concretas de data e horário. Deixar a decisão fácil. Se não responder → break-up.",
            expectedOutcomes: ["scheduled_visit", "callback_later", "gave_up", "no_answer"],
            nextStep: "Se não responder → break-up.",
        },
        {
            label: "Encerramento de Contato (break-up)",
            message: "*{name}*, encerrando o contato por aqui! 😊 Quando quiser conhecer a *{academyName}*, é só nos chamar — a porta está sempre aberta!",
            consultantAction: "ÚLTIMA MENSAGEM. Não insistir mais. Arquivar o card.",
            expectedOutcomes: ["scheduled_visit", "enrolled", "gave_up"],
            nextStep: "Arquivar.",
            isBreakup: true,
        },
    ],

    // ══════════════════════════════════════════════════════════════════════════
    // PEDIU PRAZO — solicitou extensão de pagamento
    // ══════════════════════════════════════════════════════════════════════════
    [CONTACT_REASON.DEADLINE_REQUEST]: [
        {
            label: "Confirmação do Prazo Combinado",
            message: "Olá, *{name}*! Passando para confirmar o pagamento que combinamos para *{date}* na *{academyName}*. Tudo certo por aí? 😊",
            consultantAction: "Enviar 1 dia antes da data combinada. Tom de confirmação, não de cobrança. Incluir meio de pagamento (PIX, link) junto à mensagem.",
            expectedOutcomes: ["paid", "asked_deadline", "no_answer"],
            nextStep: "Se não pagar na data → iniciar fluxo de cobrança.",
        },
        {
            label: "Prazo Perdido (não pagou na data)",
            message: "*{name}*, notamos que o pagamento combinado para *{date}* não foi realizado. Aconteceu alguma coisa? Me conta para vermos o que é possível fazer. 🙏",
            consultantAction: "Tom de entendimento, sem acusação. Oferecer reagendamento com nova data definitiva. Se insistir em não pagar → aviso de suspensão.",
            expectedOutcomes: ["paid", "asked_deadline", "gave_up", "no_answer"],
            nextStep: "Após 2 tentativas → aviso de suspensão (fluxo de Cobrança).",
        },
        {
            label: "Encerramento de Contato (break-up)",
            message: "*{name}*, encerrando as tentativas de contato. Quando puder regularizar a situação na *{academyName}*, é só nos chamar — sempre prontos para ajudar. 🤝",
            consultantAction: "ÚLTIMA MENSAGEM. Arquivar e encaminhar para processo padrão de inadimplência.",
            expectedOutcomes: ["paid", "gave_up"],
            nextStep: "Arquivar. Encaminhar para processo de inadimplência.",
            isBreakup: true,
        },
    ],

    // ══════════════════════════════════════════════════════════════════════════
    // TRANCAMENTO — aluno quer pausar a matrícula
    // ══════════════════════════════════════════════════════════════════════════
    [CONTACT_REASON.FREEZE_REQUEST]: [
        {
            label: "Confirmação do Trancamento",
            message: "Olá, *{name}*! Seu trancamento na *{academyName}* está confirmado até *{date}*. Quando estiver pronto para voltar, é só nos avisar — sua vaga está garantida! 🤝",
            consultantAction: "Confirmar período de trancamento permitido. Agendar tarefa de retorno para D-7 do fim do trancamento. Registrar data de retorno prevista.",
            expectedOutcomes: ["frozen", "callback_later"],
            nextStep: "Criar nova tarefa de Renovação para D-7 do fim do trancamento.",
        },
        {
            label: "Aviso de Fim do Trancamento (D-7)",
            message: "*{name}*, seu trancamento na *{academyName}* termina em *{days} dias*! Já está se preparando para voltar? Posso reservar seu horário? 🏃",
            consultantAction: "Enviar 7 dias antes de vencer. Se não responder em 3 dias → ligar. Objetivo: garantir que o aluno volte e não deixe a matrícula expirar por esquecimento.",
            expectedOutcomes: ["enrolled", "asked_deadline", "gave_up", "callback_later", "no_answer"],
            nextStep: "Se não responder → break-up.",
        },
        {
            label: "Encerramento de Contato (break-up)",
            message: "*{name}*, última mensagem! Seu trancamento encerrou e não conseguimos falar. Quando quiser retomar na *{academyName}*, é só chamar — estaremos aqui! 😊",
            consultantAction: "ÚLTIMA MENSAGEM. Se não responder → iniciar fluxo de Reativação em 30 dias se o aluno não retornar espontaneamente.",
            expectedOutcomes: ["enrolled", "gave_up"],
            nextStep: "Arquivar. Criar tarefa de Reativação em 30 dias.",
            isBreakup: true,
        },
    ],

    // ══════════════════════════════════════════════════════════════════════════
    // INDICAÇÃO — novo aluno veio por referência
    // ══════════════════════════════════════════════════════════════════════════
    [CONTACT_REASON.REFERRAL]: [
        {
            label: "Boas-vindas por Indicação",
            message: "Olá, *{name}*! O *{referralName}* nos indicou você e ficamos muito felizes! 😄 Gostaria de conhecer a *{academyName}* e ver como podemos te ajudar? Posso agendar uma visita ou uma aula experimental sem compromisso!",
            consultantAction: "Agir o mais rápido possível — lead indicado é o de maior conversão. Usar o nome de quem indicou. Oferecer visita ou experimental com data concreta.",
            expectedOutcomes: ["scheduled_visit", "enrolled", "callback_later", "no_answer"],
            nextStep: "Aguardar 24h. Se não responder → segunda tentativa.",
        },
        {
            label: "Segundo Contato (D+2 sem resposta)",
            message: "*{name}*, tentei falar com você ontem sobre a *{academyName}*! O *{referralName}* falou muito bem de você 😊 Ainda tem interesse em conhecer? Uma visita de 20 minutos sem compromisso já dá uma boa ideia!",
            consultantAction: "Tom leve e sem pressão. Usar o indicador como âncora social. Propor data e hora específica para a visita.",
            expectedOutcomes: ["scheduled_visit", "enrolled", "callback_later", "no_answer"],
            nextStep: "Se não responder → break-up.",
        },
        {
            label: "Encerramento de Contato (break-up)",
            message: "*{name}*, última tentativa por aqui! 😊 Se um dia quiser conhecer a *{academyName}*, é só nos chamar — a indicação do *{referralName}* continua valendo. Boa sorte!",
            consultantAction: "ÚLTIMA MENSAGEM. Não insistir mais. O indicador pode ser consultado para entender se houve algum problema.",
            expectedOutcomes: ["scheduled_visit", "enrolled", "gave_up"],
            nextStep: "Arquivar. Agradecer ao indicador independentemente do resultado.",
            isBreakup: true,
        },
    ],

    // ══════════════════════════════════════════════════════════════════════════
    // UPGRADE — aluno quer plano maior / mais serviços
    // ══════════════════════════════════════════════════════════════════════════
    [CONTACT_REASON.UPGRADE]: [
        {
            label: "Apresentação de Upgrade",
            message: "Olá, *{name}*! Vimos que você tem aproveitado bastante a *{academyName}* e gostaríamos de te apresentar algumas opções que podem tornar sua experiência ainda melhor. Tem 5 minutos? 🚀",
            consultantAction: "Apresentar o plano superior com benefícios concretos (aulas extras, personal, modalidades etc.). Calcular a diferença de valor. Não forçar — fazer o aluno enxergar o valor.",
            expectedOutcomes: ["enrolled", "callback_later", "requested_discount", "no_answer"],
            nextStep: "Enviar comparativo visual por WhatsApp. Se não responder → segunda tentativa.",
        },
        {
            label: "Segundo Contato (D+3 sem resposta)",
            message: "*{name}*, passou a semana e não consegui falar sobre as opções de upgrade na *{academyName}*! Se quiser ver o comparativo de planos, é só me pedir — mando em segundos. 😊",
            consultantAction: "Tom leve. Se não responder → break-up. Não criar pressão em quem não demonstrou interesse.",
            expectedOutcomes: ["enrolled", "callback_later", "no_answer"],
            nextStep: "Se não responder → break-up.",
        },
        {
            label: "Encerramento de Contato (break-up)",
            message: "*{name}*, encerrando o contato sobre as opções de upgrade! Quando quiser conhecer as novidades da *{academyName}*, é só chamar. 🙌",
            consultantAction: "ÚLTIMA MENSAGEM. Arquivar. O aluno pode retornar espontaneamente quando estiver pronto.",
            expectedOutcomes: ["enrolled", "gave_up"],
            nextStep: "Arquivar.",
            isBreakup: true,
        },
    ],

    // ══════════════════════════════════════════════════════════════════════════
    // RECLAMAÇÃO — aluno insatisfeito
    // ══════════════════════════════════════════════════════════════════════════
    [CONTACT_REASON.COMPLAINT]: [
        {
            label: "Abertura de Escuta (urgente — até 2h)",
            message: "Olá, *{name}*! Ficamos sabendo que você teve uma experiência ruim na *{academyName}* e queremos entender o que aconteceu. Pode me contar? Estamos comprometidos em resolver. 🙏",
            consultantAction: "PRIORIDADE MÁXIMA — responder em até 2h. Escutar sem interromper. Não justificar antes de pedir desculpa. Escalar para gestão se necessário.",
            expectedOutcomes: ["resolved", "retained", "gave_up", "callback_later"],
            nextStep: "Follow-up em 48h para confirmar que o problema foi resolvido.",
        },
        {
            label: "Confirmação de Resolução (D+2)",
            message: "*{name}*, passando para confirmar que o que conversamos foi resolvido na *{academyName}*! Ficou tudo ok por aí? Qualquer coisa, pode me chamar diretamente. 😊",
            consultantAction: "Follow-up obrigatório 48h após a resolução. Confirmar que o aluno ficou satisfeito. Se ainda insatisfeito → escalar novamente.",
            expectedOutcomes: ["resolved", "retained", "gave_up", "callback_later"],
            nextStep: "Se ainda insatisfeito → escalar para a gestão.",
        },
        {
            label: "Encerramento de Contato (break-up)",
            message: "*{name}*, fizemos o que era possível da nossa parte. Lamentamos muito pela experiência e respeitamos sua decisão. Se quiser dar mais uma chance à *{academyName}* no futuro, estaremos aqui. 🙏",
            consultantAction: "ÚLTIMA MENSAGEM. Usar somente se o aluno não ficou satisfeito após tentativas de resolução. Registrar o caso com detalhes para melhoria interna.",
            expectedOutcomes: ["retained", "gave_up"],
            nextStep: "Arquivar. Gerar relatório interno sobre o caso.",
            isBreakup: true,
        },
    ],

    // ══════════════════════════════════════════════════════════════════════════
    // VAGA DISPONÍVEL — lista de espera com vaga aberta
    // ══════════════════════════════════════════════════════════════════════════
    [CONTACT_REASON.WAITLIST_AVAILABLE]: [
        {
            label: "Aviso de Vaga (imediato)",
            message: "*{name}*, boa notícia! Abriu uma vaga na turma que você aguardava na *{academyName}*! 🎉 As vagas costumam ir rápido — posso confirmar a sua agora?",
            consultantAction: "Agir com urgência real — vagas têm validade. Dar prazo de 24-48h para a resposta. Ligar se não responder em 2h.",
            expectedOutcomes: ["enrolled", "callback_later", "gave_up", "no_answer"],
            nextStep: "Ligar se não responder em 2h. Dar prazo de 24h.",
        },
        {
            label: "Urgência (D+1 — vaga prestes a ser preenchida)",
            message: "*{name}*, a vaga na *{academyName}* ainda está disponível, mas por pouco tempo! Hoje é o último dia para confirmar. Posso reservar para você? ⏰",
            consultantAction: "URGÊNCIA REAL — se existir próximo na lista de espera, avisar que tem prazo. Se {name} não confirmar → chamar próximo.",
            expectedOutcomes: ["enrolled", "gave_up", "no_answer"],
            nextStep: "Se não responder até o prazo → chamar próximo da lista + break-up.",
        },
        {
            label: "Encerramento de Contato (break-up)",
            message: "*{name}*, a vaga foi preenchida por outra pessoa. Quando abrir uma nova, você é o próximo da lista — te avisarei! 😊",
            consultantAction: "ÚLTIMA MENSAGEM para este ciclo. Manter {name} na lista de espera para a próxima oportunidade. Não arquivar — manter como 'Agendado' para próxima abertura.",
            expectedOutcomes: ["enrolled", "callback_later"],
            nextStep: "Manter na lista. Reativar quando a próxima vaga abrir.",
            isBreakup: true,
        },
    ],
};

// ══════════════════════════════════════════════════════════════════════════════
// LEAD INTERESSE — pessoa entrou em contato, recebeu informações e não respondeu
// Fluxo: follow-up leve → benefício → última tentativa → encerramento
// Detectado quando a descrição contém "aguardando resposta" | "interesse em" |
// "informações enviadas" | "recebeu informações" | "enviou tabela"
// ══════════════════════════════════════════════════════════════════════════════
// Cadência baseada em: Glofox Fitness CRM Guide · HubSpot Sales Cadence · Salesforce Lead Response
// Lead QUENTE = alguém que buscou a academia por conta própria (pediu preço, perguntou sobre turma)
// → 1º follow-up em 24h (lead está fresco, risco de ir para concorrente)
// Lead FRIO = academia que prospecta alguém que não demonstrou interesse ativo
// → 1º follow-up em 2-5 dias (B2B/cold outreach — fonte: leadresponse.co, Tendril)
export const LEAD_INTEREST_TEMPLATES = [
    {
        label: "1º Follow-up — Lead Quente (D+1, 24h após proposta)",
        message: "Oi, *{name}*! Tudo bem? 😊\n\nFicou alguma dúvida sobre os horários ou valores da turma?\n\nPosso te ajudar a escolher a melhor opção para a aula experimental.",
        consultantAction: "LEAD QUENTE: enviar 24h após o envio das informações (não no mesmo dia). Tom leve, sem pressão, sem repetir a proposta. Objetivo: abrir espaço para dúvidas e avançar para agendamento da aula experimental. Fonte: Glofox Fitness CRM Guide (D+1).",
        expectedOutcomes: ["scheduled_visit", "callback_later", "asked_deadline", "no_answer"],
        nextStep: "Se não responder → registrar 'Não Atendeu', programar D+3 (48h depois).",
    },
    {
        label: "2ª Tentativa — Ângulo Experimental (D+3)",
        message: "Oi, *{name}* 😊\n\nPassando para saber se ainda faz sentido para vocês conhecerem a turma.\n\nComo as turmas têm número limitado de alunos, a aula experimental ajuda vocês a conhecerem o espaço e verem como a criança se adapta antes de decidir.\n\nQuer que eu verifique uma opção de horário para vocês?",
        consultantAction: "48h após o 1º follow-up sem resposta. Mude o ângulo: usar 'número limitado de alunos' (verdadeiro, sem urgência artificial). Propor aula experimental como passo de baixo risco — não pedir a matrícula direto. Fonte: Glofox (D+3).",
        expectedOutcomes: ["scheduled_visit", "callback_later", "no_answer"],
        nextStep: "Se não responder → registrar 'Não Atendeu', programar D+7 (4 dias depois).",
    },
    {
        label: "3ª Tentativa — Tom Curto (D+7)",
        message: "*{name}*, tudo bem? Consigo te ajudar a encontrar o melhor horário para vocês 😊",
        consultantAction: "4 dias após a 2ª tentativa. Mensagem mais curta e sem carga — objetivo: reabrir o diálogo com mínima fricção. Se possível, tentar canal diferente (ligação rápida em vez de mensagem). Fonte: Glofox (D+7).",
        expectedOutcomes: ["scheduled_visit", "callback_later", "no_answer"],
        nextStep: "Se não responder → registrar 'Não Atendeu', programar D+11–14 (última tentativa).",
    },
    {
        label: "4ª Tentativa — Última Antes do Encerramento (D+11–14)",
        message: "*{name}*, antes de encerrar o contato por aqui — ficou com alguma dúvida ou algo que posso esclarecer melhor? Às vezes uma conversa rápida de 2 minutos resolve 😊",
        consultantAction: "4 a 7 dias após a 3ª tentativa. Tom de curiosidade genuína, sem pressão. Se responder → retomar fluxo de agendamento. Pesquisas indicam 10–33% de resposta nesta etapa quando a mensagem é bem escrita. Fonte: HubSpot Sales Cadence.",
        expectedOutcomes: ["scheduled_visit", "enrolled", "callback_later", "no_answer"],
        nextStep: "Se não responder → encerramento elegante.",
    },
    {
        label: "Encerramento de Contato (break-up)",
        message: "*{name}*, não quero te incomodar mais 😊 Quando quiser conhecer a *{academyName}* — hoje, mês que vem ou quando for a hora certa — é só nos chamar. Será um prazer receber vocês!",
        consultantAction: "ÚLTIMA MENSAGEM. Tom caloroso, sem cobrança, porta aberta. Não contatar mais por iniciativa própria. Registrar 'Sem Resposta / Inatividade' (ou 'Desistiu' se houve recusa clara) e arquivar o card.",
        expectedOutcomes: ["scheduled_visit", "enrolled", "gave_up", "no_response_archived"],
        nextStep: "Arquivar. Reabrir somente se o lead entrar em contato.",
        isBreakup: true,
    },
];

// ══════════════════════════════════════════════════════════════════════════════
// PÓS-EXPERIMENTAL — aluno COMPARECEU à aula mas não matriculou
// Fluxo: feedback + pitch → acompanhamento → encerramento (break-up)
// Usado quando a task tem título "Pós-Experimental: ..." ou similar
// ══════════════════════════════════════════════════════════════════════════════
export const POST_EXPERIMENTAL_TEMPLATES = [
    {
        label: "Pós-Experimental: Como foi a aula?",
        message: "Oi, *{name}*! 😊 Passando para saber como foi a aula de *{activityName}* na *{academyName}*! O que você achou? Ficou com alguma dúvida sobre os planos?",
        consultantAction: "Tom de curiosidade genuína, não de venda agressiva. Ouça a resposta antes de apresentar planos. Se elogiou a aula → pergunte qual plano se encaixa melhor na rotina. Se teve ressalva → contorne a objeção antes de avançar.",
        expectedOutcomes: ["enrolled", "interested", "callback_later", "requested_discount", "no_answer"],
        nextStep: "Se não responder em 2–3 dias → segunda tentativa. Se respondeu com interesse → fechar o plano agora.",
    },
    {
        label: "Pós-Experimental: Acompanhamento (sem resposta)",
        message: "*{name}*, ainda pensando por aqui? 😊 Posso te ajudar a escolher o plano que melhor se encaixa na sua rotina na *{academyName}* — é só me dizer quantos dias por semana você pretende treinar!",
        consultantAction: "Facilitar a decisão com uma pergunta simples (dias por semana). Ter em mãos 2–3 opções de plano para enviar logo após a resposta. Se der desconto, fazer parecer exclusivo e com prazo.",
        expectedOutcomes: ["enrolled", "interested", "callback_later", "requested_discount", "gave_up", "no_answer"],
        nextStep: "Se não responder → break-up.",
    },
    {
        label: "Pós-Experimental: Encerramento (break-up)",
        message: "*{name}*, encerrando o contato por aqui! 😊 Foi ótimo ter você na *{academyName}*. Quando quiser começar, a vaga é sua — é só nos chamar!",
        consultantAction: "ÚLTIMA MENSAGEM. Tom caloroso, sem pressão. Não insistir mais. Arquivar o card.",
        expectedOutcomes: ["enrolled", "gave_up"],
        nextStep: "Arquivar.",
        isBreakup: true,
    },
];

// ══════════════════════════════════════════════════════════════════════════════
// CONFIRMAÇÃO E BOAS-VINDAS — aluno agendou aula experimental no futuro
// ══════════════════════════════════════════════════════════════════════════════
export const TRIAL_CONFIRMATION_TEMPLATES = [
    {
        label: "Boas-vindas e Orientações",
        message: "Olá, *{name}*! Tudo bem? 😊 Aqui é da *{academyName}*. Confirmamos seu agendamento de aula experimental de *{activityName}* para o dia *{date}* às *{time}*. Para a aula, recomendamos trazer roupa de banho, toalha, chinelo e touca. Qualquer dúvida, estou à disposição! Podemos confirmar sua presença? 💙",
        consultantAction: "Enviar assim que a aula experimental for agendada. Dar as boas-vindas, confirmar o horário e passar as orientações básicas de vestimenta/equipamento.",
        expectedOutcomes: ["scheduled_visit", "no_answer"],
        nextStep: "Aguardar confirmação. No dia da aula, se não tiver confirmado, usar template 'Confirmação no Dia'.",
    },
    {
        label: "Confirmação no Dia da Aula",
        message: "Oi, *{name}*! Tudo bem? 😊 Passando para confirmar sua presença na aula experimental de *{activityName}* hoje às *{time}* na *{academyName}*. Conseguimos confirmar sua ida para deixarmos tudo pronto para você? 🏊‍♀️",
        consultantAction: "Enviar na manhã do dia da aula experimental se o aluno ainda não tiver confirmado. Confirmar presença para evitar desperdício de vaga.",
        expectedOutcomes: ["scheduled_visit", "gave_up", "no_answer"],
        nextStep: "Se não comparecer → usar templates de Faltou à Aula Experimental (D+1).",
    }
];

// ══════════════════════════════════════════════════════════════════════════════
// RISCO DE EVASÃO / ALERTA DE INATIVIDADE — aluno com faltas consecutivas
// ══════════════════════════════════════════════════════════════════════════════
export const EVASION_TEMPLATES = [
    {
        label: "Abordagem Empática (Sentimos sua falta)",
        message: "Oi, *{name}*! Tudo bem? Sentimos sua falta na *{academyName}*! Reparei que você não compareceu às últimas aulas. Está tudo bem? Se precisar de alguma ajuda com horários ou para retomar sua rotina, conta comigo! 🙌",
        consultantAction: "Primeiro contato de resgate. Tom amigável e de cuidado. Focado em saber se o aluno está bem e se precisa de apoio para voltar, sem pressionar.",
        expectedOutcomes: ["retained", "callback_later", "asked_deadline", "gave_up", "no_answer"],
        nextStep: "Aguardar 2 dias. Se não responder → enviar segunda tentativa.",
    },
    {
        label: "Apoio para Retorno (Reagendar/Ajustar)",
        message: "Oi, *{name}*! Passando para ver se conseguiu se organizar para voltar às aulas na *{academyName}*. Se o horário estiver difícil ou se precisar reorganizar sua agenda, me avise para encontrarmos a melhor opção para você! 😉",
        consultantAction: "Enviar caso o aluno não tenha respondido ao primeiro contato ou se tiver indicado problemas de agenda. Oferecer flexibilidade.",
        expectedOutcomes: ["retained", "callback_later", "asked_deadline", "gave_up", "no_answer"],
        nextStep: "Aguardar 3 dias. Se não responder → break-up.",
    },
    {
        label: "Encerramento de Contato (break-up)",
        message: "Oi, *{name}*. Como não tivemos retorno, vou encerrar os contatos por aqui para não te incomodar. 😊 Mas saiba que estamos sempre de portas abertas na *{academyName}* para quando quiser voltar. Se precisar de algo, é só me chamar!",
        consultantAction: "Última tentativa de contato. Arquivar após o envio.",
        expectedOutcomes: ["retained", "gave_up", "no_response_archived"],
        nextStep: "Arquivar o card.",
        isBreakup: true,
    }
];

/**
 * Retorna os templates corretos com base no tipo e contexto da task.
 *
 * Cenários detectados (por ordem de prioridade):
 *  0. Risco de Evasão   → aluno com faltas consecutivas / inatividade
 *  1. Pós-Experimental  → aluno compareceu mas não matriculou
 *  2. Confirmação       → aula experimental agendada no futuro
 *  3. Lead Interesse    → lead perguntou, recebeu informações, ainda não agendou
 *  4. No-show           → agendou a experimental e não apareceu (padrão trial_followup)
 *  5. Outros motivos    → MEMBER_MESSAGE_TEMPLATES[contactReason]
 *
 * Palavras-chave para Lead Interesse (descrição do card):
 *   "aguardando resposta" | "interesse em" | "informações enviadas" |
 *   "recebeu informações" | "enviou tabela" | "perguntando sobre" |
 *   "ainda não agendou" | "entrou em contato perguntando"
 */
export const getTrialTemplates = (item) => {
    const title = (item?.title || '').toLowerCase();
    const desc  = (item?.description || '').toLowerCase();

    // 0. Risco de Evasão / Alerta de Inatividade (faltas consecutivas)
    if (
        title.includes('evasão') ||
        title.includes('evasao') ||
        title.includes('inatividade') ||
        desc.includes('inatividade') ||
        desc.includes('faltas consecutivas')
    ) {
        return EVASION_TEMPLATES;
    }

    // 1. Pós-Experimental (compareceu à aula, não matriculou)
    if (
        title.includes('pós-experimental') ||
        title.includes('pos-experimental') ||
        desc.includes('compareceu à aula')
    ) {
        return POST_EXPERIMENTAL_TEMPLATES;
    }

    // 2. Confirmação de aula experimental agendada
    if (
        desc.includes('nova aula experimental agendada') ||
        desc.includes('confirme a presença') ||
        title.includes('agendada')
    ) {
        return TRIAL_CONFIRMATION_TEMPLATES;
    }

    // 3. Lead com interesse — recebeu informações, ainda não agendou nem faltou
    const isLeadInterest = (
        desc.includes('aguardando resposta') ||
        desc.includes('interesse em') ||
        desc.includes('informações enviadas') ||
        desc.includes('recebeu informações') ||
        desc.includes('enviou tabela') ||
        desc.includes('perguntando sobre') ||
        desc.includes('ainda não agendou') ||
        desc.includes('entrou em contato perguntando')
    );
    if (isLeadInterest && item?.contactReason === 'trial_followup') {
        return LEAD_INTEREST_TEMPLATES;
    }

    // 4. No-show / demais trial_followup
    return MEMBER_MESSAGE_TEMPLATES[item?.contactReason] || [];
};

/**
 * Retorna os templates de um motivo de contato prontos para exibição.
 * @param {string} contactReason - valor de CONTACT_REASON
 */
export const getTemplatesForReason = (contactReason) =>
    MEMBER_MESSAGE_TEMPLATES[contactReason] || [];

/**
 * Retorna apenas o template de break-up de um motivo de contato.
 * @param {string} contactReason - valor de CONTACT_REASON
 */
export const getBreakupTemplate = (contactReason) =>
    (MEMBER_MESSAGE_TEMPLATES[contactReason] || []).find(t => t.isBreakup) || null;

/**
 * Interpola variáveis em um template de mensagem.
 * @param {string} message - texto com {variáveis}
 * @param {Object} vars - { name, academyName, days, amount, date, referralName }
 */
export const interpolateTemplate = (message, vars = {}) =>
    message.replace(/\{(\w+)\}/g, (_, key) => vars[key] ?? `{${key}}`);
